import { useEffect } from "react";
import i18n from "../../language/i18n";
import { useImageZoomPan } from "../../hooks/useImageZoomPan";
import { glassLightboxStyles } from "./glassLightboxStyles";

interface IGlassLightboxProps {
  src: string;
  alt?: string;
  onClose: () => void;
}

/**
 * Glass image lightbox with zoom and pan.
 *
 * <p>Wheel / trackpad zoom about the cursor, drag to pan, double-click to toggle fit ↔ close-up, pinch on
 * touch, and explicit −/+/reset controls for anyone who does not know the gestures. Escape and a backdrop
 * click still close it.
 *
 * <p>The gesture maths — including the "Yazı Boyutu" zoom-unit correction and the non-passive wheel
 * binding — lives in {@link useImageZoomPan}, shared with the handwritten-exam scan viewer.
 */
const GlassLightbox = ({ src, alt, onClose }: IGlassLightboxProps) => {
  const zoom = useImageZoomPan();
  const { reset } = zoom;

  // A new image starts fresh — carrying the previous one's magnification over would open an image
  // already scrolled to a corner of something the user has not seen yet.
  useEffect(() => {
    reset();
  }, [src, reset]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const onBackdropClick = () => {
    // A pan that finishes over the backdrop must not dismiss the lightbox.
    if (zoom.consumeMoved()) return;
    onClose();
  };

  return (
    // gestureEndHandlers live on the ROOT so a finger that lifts on the backdrop still ends the
    // gesture — the stage never sees that pointerup, because synthetic events bubble up, not down.
    <div
      className="alb-root"
      onClick={onBackdropClick}
      role="dialog"
      aria-modal="true"
      {...zoom.gestureEndHandlers}
    >
      <style>{glassLightboxStyles}</style>
      <button
        type="button"
        className="alb-close"
        onClick={onClose}
        aria-label={i18n.t("close")}
      >
        ✕
      </button>

      {/* The controls sit ON the overlay, not inside the stage, so clicking one never reaches the
          backdrop's close handler. */}
      <div className="alb-tools" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="alb-tool"
          onClick={() => zoom.zoomFromCentre(zoom.scale / 1.4)}
          disabled={!zoom.isZoomed}
          aria-label={i18n.t("zoom_out")}
        >
          −
        </button>
        <button
          type="button"
          className="alb-tool alb-tool--value"
          onClick={zoom.reset}
          disabled={!zoom.isZoomed}
          aria-label={i18n.t("zoom_reset")}
        >
          %{Math.round(zoom.scale * 100)}
        </button>
        <button
          type="button"
          className="alb-tool"
          onClick={() => zoom.zoomFromCentre(zoom.scale * 1.4)}
          aria-label={i18n.t("zoom_in")}
        >
          +
        </button>
      </div>

      <div
        ref={zoom.stageRef}
        className={`alb-stage${zoom.isZoomed ? " alb-stage--zoomed" : ""}`}
        {...zoom.stageHandlers}
        // Declared AFTER the spread on purpose: this onClick must win. Today stageHandlers carries no
        // onClick, so the order is cosmetic — but if one is ever added there, spreading last would
        // silently replace this handler and the backdrop-click guard below would stop running.
        onClick={(event) => {
          event.stopPropagation();
          // Consume the "this gesture moved" flag HERE, because a pan that ends inside the image
          // never reaches the backdrop handler that would otherwise clear it — this stopPropagation
          // is what stops it. Left set, the flag swallowed the NEXT genuine backdrop click, so after
          // panning the user had to click outside twice to close.
          zoom.consumeMoved();
        }}
      >
        <img
          src={src}
          // Falls back to the generic "image" noun, never to a control label: "Yakınlaştır" described
          // the zoom BUTTON to a screen reader rather than the picture it was reading out.
          alt={alt || i18n.t("image")}
          draggable={false}
          style={{ transform: zoom.transform }}
        />
      </div>
      <p className="alb-hint">{i18n.t("lightbox_zoom_hint")}</p>
    </div>
  );
};

export default GlassLightbox;
